import { useCallback, useEffect, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { ChevronLeft, ChevronRight, Camera } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { useWidget } from "../../../providers/widget";
import { GalleryWidgetConfig } from "../config";
import { useFetchGalleryImages } from '../data'
import { Carousel as CarouselUI } from '../../../components/ui/carousel'
import ShowAllPhotosButton from './masonry/ShowAllPhotosButton'
import { useGallery, OPEN_AT_TOP } from '../providers';
import { getProxiedImageUrl } from '../../../common/image-proxy';

interface CarouselProps {
  imagesToSlide?: number
  className?: string;
}

export default function Carousel({ 
  imagesToSlide = 1, 
  className,
}: CarouselProps) {
  const { options } = useWidget<GalleryWidgetConfig>()
  const { setFullScreenImageIndex } = useGallery()
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: imagesToSlide,
    containScroll: 'trimSnaps',
    breakpoints: {
      '(min-width: 1024px)': { slidesToScroll: imagesToSlide },
      '(min-width: 768px)': { slidesToScroll: imagesToSlide },
      '(max-width: 767px)': { slidesToScroll: 1 }
    }
  })

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])

  // Fetch gallery images
  const { data } = useFetchGalleryImages(options.filter)
  const images = data?.pages.flatMap(page => page.result) || []

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  // const scrollTo = useCallback((index: number) => {
  //   if (emblaApi) emblaApi.scrollTo(index)
  // }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    setScrollSnaps(emblaApi.scrollSnapList())
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
  }, [emblaApi, onSelect])

  // Responsive images to show based on screen size
  const getResponsiveImagesToShow = () => {
    if (typeof window === 'undefined') return options.carousel.imagesToShow??1
    
    if (window.innerWidth >= 1200) return options.carousel.imagesToShow1200??4
    if (window.innerWidth >= 780) return options.carousel.imagesToShow780??3
    return 1
  }

  // Seed with the SSR-safe value (the window===undefined branch) so the client's
  // first render matches the server; the effect below sets the real
  // viewport-derived value on mount. Reading window.innerWidth in the
  // initializer would diverge per-slide flexBasis/paddingRight styles (#771).
  const [responsiveImagesToShow, setResponsiveImagesToShow] = useState(options.carousel.imagesToShow ?? 1)

  useEffect(() => {
    const handleResize = () => {
      setResponsiveImagesToShow(getResponsiveImagesToShow())
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [options.carousel.imagesToShow, options.carousel.imagesToShow780, options.carousel.imagesToShow1200])

  if (!images.length) {
    return null
  }

  return (
    <div className={cn('homerunner-gallery-carousel w-full', className)}>
      <div className="homerunner-gallery-carousel-wrapper relative">
        {/* Navigation Buttons */}
        {/* {selectedIndex !== 0 && ( */}
        <button
          className="homerunner-gallery-carousel-nav homerunner-gallery-carousel-nav-prev absolute left-4 top-1/2 -translate-y-1/2 z-10 rounded-full p-1 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-110 disabled:hover:scale-100 homerunner-carousel-arrow"
          onClick={() => {
            scrollPrev()
            setFullScreenImageIndex(undefined)
          }}
          disabled={selectedIndex === 0}
          aria-label="Previous images"
        >
          <ChevronLeft className="h-10 w-10" />
        </button>
        {/* )} */}
        {/* {!(selectedIndex >= scrollSnaps.length - 1) && ( */}
        <button
          className="homerunner-gallery-carousel-nav homerunner-gallery-carousel-nav-next absolute right-4 top-1/2 -translate-y-1/2 z-10 rounded-full p-1 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed hover:scale-110 disabled:hover:scale-100 homerunner-carousel-arrow"
          onClick={() => {
            scrollNext()
            setFullScreenImageIndex(undefined)
          }}
          disabled={selectedIndex >= scrollSnaps.length - 1}
          aria-label="Next images"
        >
          <ChevronRight className="h-10 w-10" />
        </button>
        {/* )} */}
        {/* Carousel Container */}
        <CarouselUI className="homerunner-gallery-carousel-container overflow-hidden rounded-lg" ref={emblaRef}>
          <div className="homerunner-gallery-carousel-track flex">
            {images.map((image, index) => (
              <div 
                key={image.id+'-'+index} 
                className="homerunner-gallery-carousel-slide flex-[0_0_auto] min-w-0"
                style={{ 
                  flexBasis: `${100 / responsiveImagesToShow}%`,
                  paddingRight: (index < images.length - 1 && responsiveImagesToShow !== 1) ? '0.5rem' : '0'
                }}
              >
                <div className="homerunner-gallery-image-container gallery-image-container aspect-[4/3] rounded-lg" onClick={() => {
                  setFullScreenImageIndex(index)
                }}>
                  <img
                    src={getProxiedImageUrl(image.url, 600, 450)}
                    alt={image.caption || `Property image ${index + 1}`}
                    className="homerunner-gallery-image w-full h-full object-cover rounded-lg"
                    loading={options.lazyLoading ? "lazy" : "eager"}

                    onError={(e) => {
                      const target = e.target as HTMLImageElement
                      target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkltYWdlIG5vdCBmb3VuZDwvdGV4dD48L3N2Zz4='
                    }}
                  />
                  
                  {/* Hover overlay */}
                  <div className="homerunner-gallery-hover-overlay gallery-hover-overlay rounded-lg"></div>
                  
                  {/* Show all photos button on first image */}
                  {index === 0 && options.showAllPhotoButton && (
                    <div className="homerunner-gallery-show-all-button-container">
                      <ShowAllPhotosButton 
                        onClick={() => setFullScreenImageIndex(OPEN_AT_TOP)}
                        className="bottom-4 left-4"
                      />
                    </div>
                  )}
                  
                </div>
              </div>
            ))}
          </div>
        </CarouselUI>
      </div>
    </div>
  )
}
